/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'

import {Box, Flex, Stack, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

import {SegmentContext} from '../../integrations/segment/app/contexts'
import {mainMenuItemSelected} from '../../integrations/segment/app/page/home'
import {ListMenuPopover} from '../list-menu/list-menu-popover'
import {categoryUrlBuilder} from '../../utils/url'

/**
 * This is the navigation component used for desktop devices. Holds the site navigation,
 * providing users with a way to access all product categories and other important pages.
 * The submenus are open when the user moves the mouse over the trigger and for A11y when
 * users use the keyboard Tab key to focus over the chevron icon and press Enter.
 *
 * @param root
 */
const ListMenu = ({root, contentComponent, itemsKey, itemsCountKey, hoverDelay}) => {
    const segmentContext = useContext(SegmentContext)
    const theme = useTheme()
    const [ariaBusy, setAriaBusy] = useState(true)
    const [activeLink, setActiveLink] = useState()
    const intl = useIntl()

    const {baseStyle} = theme.components.ListMenu
    const items = root?.[itemsKey]

    useEffect(() => {
        setAriaBusy(false)
    }, [])

    return (
        <nav
            id="list-menu"
            aria-label={intl.formatMessage({
                id: 'list_menu.nav.assistive_msg',
                defaultMessage: 'Main navigation'
            })}
            aria-live="polite"
            aria-busy={ariaBusy}
            aria-atomic="true"
        >
            <Flex justify="center" style={{background: '#141519'}}>
                {items && (
                    <Stack direction={'row'} spacing={0} {...baseStyle.stackContainer}>
                        {items?.map?.((item) => {
                            const {id, name} = item
                            const itemsCount = item[itemsCountKey] || item[itemsKey]?.length || 0

                            return (
                                <Box key={id}>
                                    {itemsCount > 0 ? (
                                        <ListMenuPopover
                                            key={id}
                                            item={item}
                                            name={name}
                                            items={item?.[itemsKey]}
                                            itemsKey={itemsKey}
                                            contentComponent={contentComponent}
                                            hoverDelay={hoverDelay}
                                        />
                                    ) : (
                                        <Link
                                            as={RouteLink}
                                            to={categoryUrlBuilder(item)}
                                            onMouseOver={setActiveLink.bind(this, id)}
                                            onMouseOut={setActiveLink.bind(this)}
                                            onClick={() => {
                                                mainMenuItemSelected(item, segmentContext)
                                            }}
                                            {...baseStyle.listMenuTriggerLink}
                                            {...{name: name + ' __'}}
                                            {...(activeLink === id
                                                ? baseStyle.listMenuTriggerlessLinkActive
                                                : {})}
                                            className="level-1-link"
                                        >
                                            {name}
                                        </Link>
                                    )}
                                </Box>
                            )
                        })}
                    </Stack>
                )}
            </Flex>
        </nav>
    )
}

ListMenu.displayName = 'ListMenu'

ListMenu.propTypes = {
    root: PropTypes.object,
    itemsKey: PropTypes.string,
    itemsCountKey: PropTypes.string,
    contentComponent: PropTypes.elementType,
    hoverDelay: PropTypes.number
}

export {ListMenu}
